import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IconChevronLeft, IconChevronRight, IconRefresh, IconX} from '@tabler/icons-react';
import {useGetAuditLog} from '../../api/audit-log-api/audit-log-api';
import type {AuditLog as AuditLogEntry} from '../../api/model';
import styles from './AuditLog.module.scss';

const PAGE_SIZE = 25;

const jsonValue = (value: AuditLogEntry['oldData'] | AuditLogEntry['newData']) => {
    if (value == null) return '—';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const AuditLog: React.FC = () => {
    const {t} = useTranslation('settings', {keyPrefix: 'user.admin.audit'});
    const [table, setTable] = useState('');
    const [entityId, setEntityId] = useState('');
    const [operation, setOperation] = useState('');
    const [filters, setFilters] = useState({table: '', entityId: ''});
    const [page, setPage] = useState(0);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const query = useGetAuditLog({
        page,
        size: PAGE_SIZE,
        ...(filters.table ? {table: filters.table} : {}),
        ...(filters.entityId ? {entityId: filters.entityId} : {}),
    });
    const logs = query.data ?? [];
    const visibleLogs = operation ? logs.filter(log => log.operation?.toUpperCase() === operation) : logs;

    const applyFilters = (event: React.FormEvent) => {
        event.preventDefault();
        setFilters({table: table.trim(), entityId: entityId.trim()});
        setPage(0);
        setExpandedId(null);
    };
    const clearFilters = () => {
        setTable('');
        setEntityId('');
        setOperation('');
        setFilters({table: '', entityId: ''});
        setPage(0);
        setExpandedId(null);
    };
    const formatDate = (value?: string) => {
        if (!value) return '—';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'medium'
        }).format(date);
    };

    return <div className={styles.audit}>
        <div className={styles.heading}>
            <div><h2>{t('title')}</h2><p>{t('description')}</p></div>
            <button type="button" className={styles.button} onClick={() => query.refetch()} disabled={query.isFetching}>
                <IconRefresh size={14} className={query.isFetching ? styles.spinning : ''}/>{t('refresh')}</button>
        </div>
        <form className={styles.filters} onSubmit={applyFilters}>
            <label><span>{t('filters.table')}</span><input value={table} onChange={e => setTable(e.target.value)}
                                                           placeholder={t('filters.tablePlaceholder')}/></label>
            <label><span>{t('filters.entityId')}</span><input value={entityId}
                                                              onChange={e => setEntityId(e.target.value)}
                                                              placeholder={t('filters.entityPlaceholder')}/></label>
            <label><span>{t('filters.operation')}</span><select value={operation}
                                                                onChange={e => setOperation(e.target.value)}>
                <option value="">{t('filters.allOperations')}</option>
                {['INSERT', 'UPDATE', 'DELETE'].map(value => <option key={value}>{value}</option>)}</select></label>
            <button type="submit" className={`${styles.button} ${styles.primary}`}>{t('filters.apply')}</button>
            {(table || entityId || operation || filters.table || filters.entityId) &&
                <button type="button" className={styles.button} onClick={clearFilters}><IconX
                    size={14}/>{t('filters.clear')}</button>}
        </form>
        <div className={styles.table}>
            <div className={styles.tableHead}>
                <span>{t('columns.time')}</span><span>{t('columns.operation')}</span><span>{t('columns.table')}</span><span>{t('columns.entity')}</span><span>{t('columns.user')}</span>
            </div>
            {query.isLoading ? <div className={styles.state}>{t('loading')}</div> : query.isError ?
                <div className={`${styles.state} ${styles.error}`}>{t('error')}</div> : visibleLogs.length === 0 ?
                    <div className={styles.state}>{t('empty')}</div> : visibleLogs.map((log, index) => {
                        const id = String(log.id ?? `${page}-${index}`);
                        const expanded = id === expandedId;
                        return <div className={styles.entry} key={id}>
                            <button type="button" className={styles.row}
                                    onClick={() => setExpandedId(expanded ? null : id)} aria-expanded={expanded}>
                                <span>{formatDate(log.changedAt)}</span><span><b
                                data-operation={log.operation?.toUpperCase()}>{log.operation || '—'}</b></span><span
                                title={log.tableName}>{log.tableName || '—'}</span><span
                                title={log.entityId}>{log.entityId || '—'}</span><span
                                title={log.changedBy}>{log.changedBy || log.changedByUserId || '—'}</span></button>
                            {expanded && <div className={styles.details}>
                                <div><span>{t('oldData')}</span>
                                    <pre>{jsonValue(log.oldData)}</pre>
                                </div>
                                <div><span>{t('newData')}</span>
                                    <pre>{jsonValue(log.newData)}</pre>
                                </div>
                                {log.transactionId != null && <small>{t('transaction')}: {log.transactionId}</small>}
                            </div>}
                        </div>;
                    })}
            <div className={styles.pager}><span>{t('page', {page: page + 1})}</span>
                <div>
                    <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0 || query.isFetching} aria-label={t('previous')}><IconChevronLeft
                        size={14}/></button>
                    <button type="button" onClick={() => setPage(p => p + 1)}
                            disabled={logs.length < PAGE_SIZE || query.isFetching} aria-label={t('next')}>
                        <IconChevronRight size={14}/></button>
                </div>
            </div>
        </div>
    </div>;
};

export default AuditLog;
